import type { Interpolation, SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { Children, useState } from 'react';

import type { Theme } from '../../theme';
import { useDesignSystemTheme } from '../Hooks';
import { Popover } from '../Popover';
import type { PopoverProps } from '../Popover/Popover';
import { Tag } from '../Tag';
import { Tooltip } from '../Tooltip';
export interface OverflowProps extends PopoverProps {
  /** Used for components like Tag which have already have margins */
  noMargin?: boolean;
  children: React.ReactNode;
}

export const Overflow = ({ children, noMargin = false, ...props }: OverflowProps): React.ReactElement => {
  const { theme } = useDesignSystemTheme();
  const [showTooltip, setShowTooltip] = useState(true);

  const childrenList = children && Children.toArray(children);

  if (!childrenList || childrenList.length === 0) {
    return <>{children}</>;
  }

  const firstItem = childrenList[0];
  const additionalItems = childrenList.splice(1);

  let trigger = (
    <span css={{ lineHeight: 0 }}>
      <Popover.Trigger asChild>
        {/* button is needed so that the popover will open when the trigger is clicked */}
        <button
          css={{
            border: 'none',
            padding: 0,
            background: 'transparent',
          }}
        >
          <Tag css={getTagStyles(theme)}>+{additionalItems.length}</Tag>
        </button>
      </Popover.Trigger>
    </span>
  );
  if (showTooltip) {
    trigger = <Tooltip title="See more items">{trigger}</Tooltip>;
  }

  return additionalItems.length === 0 ? (
    <>{firstItem}</>
  ) : (
    <div css={{ display: 'inline-flex', alignItems: 'center', gap: noMargin ? 0 : theme.spacing.sm }}>
      {firstItem}
      {additionalItems.length > 0 && (
        <Popover.Root onOpenChange={(open) => setShowTooltip(!open)}>
          {trigger}
          <Popover.Content align="start" {...props}>
            <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
              {additionalItems.map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </div>
          </Popover.Content>
        </Popover.Root>
      )}
    </div>
  );
};

const getTagStyles = (theme: Theme): SerializedStyles => {
  const styles: Interpolation<Theme> = {
    marginRight: 0,
    color: theme.colors.actionTertiaryTextDefault,
    cursor: 'pointer',

    '&:focus': {
      color: theme.colors.actionTertiaryTextDefault,
    },

    '&:hover': {
      color: theme.colors.actionTertiaryTextHover,
    },

    '&:active': {
      color: theme.colors.actionTertiaryTextPress,
    },
  };

  return css(styles);
};
